import React from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";

interface EventsSearchBarProps {
  showSearchBar: boolean;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
}

/**
 * EventsSearchBar Component
 *
 * Search input for filtering events by name, description, or venue
 */
const EventsSearchBar: React.FC<EventsSearchBarProps> = ({
  showSearchBar,
  searchQuery,
  onSearchChange,
  onClearSearch,
}) => {
  return (
    <Fade in={showSearchBar}>
      <Box
        sx={{
          mb: 4,
          p: 2.5,
          bgcolor: "white",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 12px rgba(26, 35, 126, 0.08)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(26, 35, 126, 0.12)",
            borderColor: "primary.light",
          },
        }}
      >
        <TextField
          fullWidth
          placeholder="Search events by name, description, or venue..."
          value={searchQuery}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  sx={{
                    fontSize: 26,
                    color: "primary.main",
                    transition: "transform 0.2s ease",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={onClearSearch}
                  edge="end"
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                      transform: "rotate(90deg)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.default",
              borderRadius: 2,
              fontSize: "1rem",
              transition: "all 0.3s ease",
              "& fieldset": { border: "none" },
              "&:hover": {
                bgcolor: "white",
              },
              "&.Mui-focused": {
                bgcolor: "white",
                "& .MuiInputAdornment-root svg": {
                  transform: "scale(1.1)",
                },
              },
            },
          }}
        />
      </Box>
    </Fade>
  );
};

export default EventsSearchBar;
